/**
 * CuratorExhibitorsPage — управление экспонентами с точки зрения куратора.
 * Основной акцент: статус готовности (профиль, QR, представители),
 * массовые действия, отправка напоминаний.
 */
import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Search, CheckCircle2, AlertCircle, Clock, QrCode, Users,
  ChevronRight, Send, Download, Filter, X, Phone, Mail,
  Building2, MapPin, Pencil, Loader2,
} from "lucide-react";
import type { CompanySummary } from "@mvk/shared";
import { useAdminEvent } from "../../hooks/useAdminEvent";
import { tryLoadExhibitors } from "../../../services/adminEventsApi";
import { catalogService } from "../../../services/catalogService";
import type { Company } from "../../../types";
import { CompanyAvatar } from "../../../components/CompanyAvatar";

type StatusFilter = "all" | "ready" | "no_reps" | "no_qr" | "incomplete";

function summaryToCompany(summary: CompanySummary, eventId: string): Company {
  return {
    id: summary.id,
    eventId,
    name: summary.name,
    category: summary.category,
    tags: [],
    hall: summary.hall ?? "",
    booth: summary.booth ?? "",
    shortAbout: summary.shortAbout,
    logoUrl: summary.logoUrl,
    products: [],
    contact: {},
    qrCodeValue: `company:${summary.id}`,
    mapPoint: { x: 0, y: 0, hallId: "" },
  };
}

type ExhibitorStatus = "ready" | "no_reps" | "no_qr" | "incomplete";

function getStatus(company: Company, repCount: number): ExhibitorStatus {
  const profileComplete = !!(company.shortAbout && company.contact.phone && company.contact.email);
  const qrReady = !!company.qrCodeValue;
  if (profileComplete && qrReady && repCount > 0) return "ready";
  if (profileComplete && qrReady) return "no_reps";
  if (profileComplete) return "no_qr";
  return "incomplete";
}

const STATUS_UI: Record<ExhibitorStatus, {
  label: string;
  icon: React.ReactElement;
  color: string;
  bg: string;
  border: string;
}> = {
  ready:      { label: "Готов",          icon: <CheckCircle2 size={14} />, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
  no_reps:    { label: "Нет бейджей",    icon: <Users size={14} />,        color: "text-amber-600",  bg: "bg-amber-50",  border: "border-amber-100" },
  no_qr:      { label: "Нет QR",         icon: <QrCode size={14} />,       color: "text-orange-600", bg: "bg-orange-50", border: "border-orange-100" },
  incomplete: { label: "Не заполнен",    icon: <AlertCircle size={14} />,  color: "text-red-600",    bg: "bg-red-50",    border: "border-red-100" },
};

function ReminderModal({ companies, onClose }: { companies: Company[]; onClose: () => void }) {
  const [text, setText] = useState(
    "Уважаемые коллеги, просим завершить заполнение профиля вашего стенда до начала мероприятия. Профиль должен содержать: описание компании, контактные данные и хотя бы одного представителя с бейджем."
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-[15px] text-gray-900">Напоминание экспонентам</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="p-3 rounded-xl bg-amber-50 border border-amber-100 text-[12px] text-amber-700">
            Будет отправлено <strong>{companies.length}</strong> экспонентам, у которых не полностью заполнен профиль.
          </div>
          <div>
            <label className="text-[12px] text-gray-500 mb-1.5 block">Текст письма</label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5}
              className="w-full px-3 py-2 rounded-xl border border-gray-200 text-[13px] outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 resize-none" />
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {companies.slice(0, 5).map((c) => (
              <div key={c.id} className="flex items-center gap-2 text-[12px] text-gray-600">
                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                {c.name} · {c.contact.email || "нет email"}
              </div>
            ))}
            {companies.length > 5 && (
              <div className="text-[11px] text-gray-400 pl-3.5">+{companies.length - 5} ещё</div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100">Отмена</button>
          <button className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-[13px]">
            <Send size={14} /> Отправить напоминание
          </button>
        </div>
      </div>
    </div>
  );
}

export function CuratorExhibitorsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { event, loading: eventLoading } = useAdminEvent(id);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [showReminder, setShowReminder] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);
  const [repCounts, setRepCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await tryLoadExhibitors(id);
        const companies = (list ?? []).map((s) => summaryToCompany(s, id));
        if (cancelled) return;
        setAllCompanies(companies);
        const entries = await Promise.all(
          companies.map(async (c) => {
            try {
              const reps = await catalogService.listRepresentatives(id, c.id, "admin");
              return [c.id, reps.length] as const;
            } catch {
              return [c.id, 0] as const;
            }
          }),
        );
        if (!cancelled) setRepCounts(Object.fromEntries(entries));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const withStatus = useMemo(
    () =>
      allCompanies.map((c) => ({
        company: c,
        status: getStatus(c, repCounts[c.id] ?? 0),
        reps: Array.from({ length: repCounts[c.id] ?? 0 }),
      })),
    [allCompanies, repCounts],
  );

  const filtered = useMemo(() => {
    return withStatus.filter(({ company, status }) => {
      const matchSearch = company.name.toLowerCase().includes(search.toLowerCase()) ||
        company.booth.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [withStatus, search, statusFilter]);

  const incompleteCompanies = withStatus.filter(({ status }) => status !== "ready").map(({ company }) => company);

  const toggleSelect = (id: string) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  if (eventLoading || loading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!event) return <div className="p-6 text-gray-500">Мероприятие не найдено</div>;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      {showReminder && <ReminderModal companies={incompleteCompanies} onClose={() => setShowReminder(false)} />}

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-[19px] text-gray-900">Экспоненты</h1>
          <p className="text-[12px] text-gray-400 mt-0.5">{event.title} · {allCompanies.length} компаний</p>
        </div>
        <div className="flex items-center gap-2">
          {incompleteCompanies.length > 0 && (
            <button onClick={() => setShowReminder(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 text-[12px] transition-colors">
              <Send size={13} /> Напомнить ({incompleteCompanies.length})
            </button>
          )}
          <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-[13px] hover:border-indigo-200 hover:text-indigo-600 transition-colors hidden sm:flex">
            <Download size={14} /> QR все
          </button>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="flex items-center gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
        {([
          ["all", "Все", allCompanies.length],
          ["ready", "Готовы", withStatus.filter((s) => s.status === "ready").length],
          ["no_reps", "Нет бейджей", withStatus.filter((s) => s.status === "no_reps").length],
          ["incomplete", "Не заполнены", withStatus.filter((s) => s.status === "incomplete").length],
        ] as [StatusFilter, string, number][]).map(([val, label, count]) => (
          <button
            key={val}
            onClick={() => setStatusFilter(val)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] transition-colors ${
              statusFilter === val
                ? "bg-indigo-600 text-white"
                : "bg-white border border-gray-200 text-gray-600 hover:border-indigo-200"
            }`}
          >
            {label}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${statusFilter === val ? "bg-white/20" : "bg-gray-100"}`}>
              {count}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
        <Search size={14} className="text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по названию, стенду..."
          className="flex-1 text-[13px] outline-none text-gray-700 placeholder-gray-400" />
        {search && <button onClick={() => setSearch("")}><X size={14} className="text-gray-400" /></button>}
      </div>

      {/* Count */}
      <div className="text-[12px] text-gray-400">{filtered.length} из {allCompanies.length}</div>

      {/* Company cards */}
      <div className="space-y-2">
        {filtered.map(({ company, status, reps }) => {
          const ui = STATUS_UI[status];
          const isSelected = selected.has(company.id);
          return (
            <div key={company.id}
              className={`bg-white rounded-xl border transition-colors ${isSelected ? "border-indigo-300 ring-1 ring-indigo-200" : "border-gray-100 hover:border-gray-200"}`}>
              <div className="p-3 sm:p-4">
                <div className="flex items-start gap-3">
                  {/* Select checkbox */}
                  <button onClick={() => toggleSelect(company.id)}
                    className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                      isSelected ? "bg-indigo-600 border-indigo-600" : "border-2 border-gray-200 hover:border-indigo-400"
                    }`}>
                    {isSelected && <div className="w-2 h-2 rounded-sm bg-white" />}
                  </button>

                  {/* Avatar */}
                  <CompanyAvatar
                    name={company.name}
                    logoUrl={company.logoUrl}
                    size="md"
                    className="rounded-xl flex-shrink-0"
                  />

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="text-[14px] text-gray-900">{company.name}</div>
                        <div className="text-[12px] text-gray-400">{company.category}</div>
                      </div>
                      {/* Status badge */}
                      <div className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full flex-shrink-0 ${ui.bg} ${ui.color} border ${ui.border}`}>
                        {ui.icon} {ui.label}
                      </div>
                    </div>

                    {/* Details row */}
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-[11px] text-gray-400">
                      <span className="flex items-center gap-1">
                        <MapPin size={10} /> {company.booth} · Пав. {company.hall}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={10} /> {reps.length} представителей
                      </span>
                      {company.contact.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={10} /> {company.contact.phone}
                        </span>
                      )}
                    </div>

                    {/* Checklist */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {[
                        { ok: !!(company.shortAbout && company.contact.phone), label: "Профиль" },
                        { ok: !!company.qrCodeValue, label: "QR стенда" },
                        { ok: reps.length > 0, label: "Бейджи" },
                        { ok: false, label: "Telegram" },
                      ].map((check) => (
                        <span key={check.label}
                          className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-md ${
                            check.ok ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-400"
                          }`}>
                          {check.ok
                            ? <CheckCircle2 size={9} />
                            : <Clock size={9} />
                          }
                          {check.label}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => navigate(`/events/${id}/exhibitors/${company.id}`)}
                      className="p-2 rounded-lg hover:bg-indigo-50 text-gray-400 hover:text-indigo-500 transition-colors"
                    >
                      <Pencil size={15} />
                    </button>
                    <button
                      onClick={() => navigate(`/events/${id}/exhibitors/${company.id}`)}
                      className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 transition-colors"
                    >
                      <ChevronRight size={15} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Bulk actions bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-20 md:bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl z-30">
          <span className="text-[13px]">{selected.size} выбрано</span>
          <div className="w-px h-4 bg-white/20" />
          <button className="flex items-center gap-1.5 text-[12px] hover:text-indigo-300 transition-colors">
            <Send size={13} /> Напомнить
          </button>
          <button className="flex items-center gap-1.5 text-[12px] hover:text-indigo-300 transition-colors">
            <Download size={13} /> QR
          </button>
          <button onClick={() => setSelected(new Set())} className="ml-1 text-white/50 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
